import { TimingMarker } from "@faire/web/common/timings/TimingMarker";
import React, { createContext, useContext, useRef } from "react";

/**
 * Currently we don't have any segments for the action timings,
 * so we only care about the start time & end time.
 * In the future, we may add segments for UPLT actions.
 */
type ActionSegments = {
  startTime: TimingMarker;
};

export type ActionTiming = ActionSegments & {
  variant?: string;
};

/**
 * Keys should be composite in the form `eventName-elementId-actionId` where
 *  - `eventName` is the name of the event that triggered the action.
 *  - `elementId` is a unique id that the "detector" element has access to.
 *  - `actionId` is a unique value for the action.
 *
 * For add-to-cart the key could be `add-to-cart-{productToken}-{quantity}`
 * since
 *  - elements that reflect add-to-cart completions have access to the product token
 *  - the action is uniquely identified by the product token and quantity.
 */
type ActionContext = Record<string, ActionTiming>;

export const AddToCartTrackingContext = createContext<ActionContext>({});

export const useAddToCartTrackingContext = () =>
  useContext(AddToCartTrackingContext);

export const AddToCartTrackingContextProvider: React.FC<{
  children: React.ReactNode;
  value?: ActionContext;
}> = ({ children, ...props }) => {
  // Using a ref to reliably persist across renders
  const value = useRef<ActionContext>({}).current;
  return (
    <AddToCartTrackingContext.Provider value={props.value ?? value}>
      {children}
    </AddToCartTrackingContext.Provider>
  );
};
